import { Dispatch } from 'types/dispatch';
import { State } from 'widgets/redeem/root/root.reducers';
import { IUplayClientProduct } from './pyc-redeem.types';
import { IContentPackItem } from '@sitegen/common/types';
import { IRedeemCodeData } from 'types/redemption';

import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';
import {
    ADDITIONAL_DETAILS_STATUS,
    GO_TO_ITEM_SELECTION_PAGE,
    GO_TO_ITEMS_DISPLAY_PAGE,
    OWNERSHIP_STATUS,
    SELECTED_ITEM_KEY,
    SET_ADDITIONAL_DETAILS_STATUS,
    SET_OWNERSHIP_STATUS,
    SET_PYC_GAME_ITEMS,
    SET_SELECTED_GAME_ITEM_ID
} from './pyc-redeem.constants';

import { redeemGift, setRedemptionStatus, submitCode } from '../redemption/redemption.actions';
import { PYCRedeemAPIService } from './pyc-redeem.service';
import { getAvailableContentPackItems, matchGamesByStatus } from './pyc-redeem.utils';
import { REDEMPTION_STATUS } from '../redemption/redemption.constants';

export const goToItemsDisplayPage = () => ({
    type: GO_TO_ITEMS_DISPLAY_PAGE
});

const goToItemSelectionPage = () => ({
    type: GO_TO_ITEM_SELECTION_PAGE
});

const setSelectedGameItemId = (payload: string) => ({
    type: SET_SELECTED_GAME_ITEM_ID,
    payload
});

const setPYCRedeemGameItems = (payload: IContentPackItem[]) => ({
    type: SET_PYC_GAME_ITEMS,
    payload
});

const setOwnershipStatus = (payload: OWNERSHIP_STATUS) => ({
    type: SET_OWNERSHIP_STATUS,
    payload
});

export const setAdditionalDetailsStatus = (payload: ADDITIONAL_DETAILS_STATUS) => ({
    type: SET_ADDITIONAL_DETAILS_STATUS,
    payload
});

export const selectGameItem = (packId: string) => async (dispatch: Dispatch): Promise<void> => {
    dispatch(setSelectedGameItemId(packId));
    dispatch(goToItemSelectionPage());
};

export const submitGameItem = () => async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const state: State = getState();
    const selectedGameId = state.PYCRedeemReducer.selectedGameItemId;
    const code = state.redemption.redeemData?.code!;

    const redeemData: IRedeemCodeData = {
        platform: PLATFORM_TYPES.PC,
        code,
        details: [{ key: SELECTED_ITEM_KEY, value: String(selectedGameId)! }]
    };
    dispatch(redeemGift(redeemData));
};

export const fetchRedeemGameIdsData = () => async (dispatch: Dispatch, getState: () => State) => {
    const state: State = getState();
    const countryCode = state.IPLocation?.data?.countryCode || '';
    const availableItems = state.redemption.availableActions ?? [];

    const contentPackItems = getAvailableContentPackItems(
        state.config.contentPackItems || [],
        availableItems
    );

    PYCRedeemAPIService.configure({
        appId: state.config.applicationId,
        env: state.config.envName,
        countryCode,
        session: {
            ticket: state.login.loginSession?.UbisoftConnectTicket!,
        }
    });

    try {
        const result: IUplayClientProduct[] = await PYCRedeemAPIService.getPYCRedeemProducts();
        const matchedGameItems = matchGamesByStatus(contentPackItems, result);

        dispatch(setPYCRedeemGameItems(matchedGameItems));

        const hasAllOwnedGames = matchedGameItems.every(item => item.alreadyOwned);
        const ownershipStatus = hasAllOwnedGames ?
            OWNERSHIP_STATUS.OWNED_ALL_GAMES :
            OWNERSHIP_STATUS.OWNED_NOT_ALL_GAMES;

        dispatch(setOwnershipStatus(ownershipStatus));
    } catch (e) {
        dispatch(setRedemptionStatus({ status: REDEMPTION_STATUS.GENERIC_ERROR }));
    }
};

/** First PYC Redeem API Call, for valid code always sets status to Additional Info */
export const redeemCode = () => async (dispatch: Dispatch, getState: () => State) => {
    const state: State = getState();
    const code = state.redemption.redeemData?.code!;
    await dispatch(submitCode(code));
};

export type PYCRedeemActions = ReturnType<typeof setSelectedGameItemId>
| ReturnType<typeof setPYCRedeemGameItems>
| ReturnType<typeof goToItemsDisplayPage>
| ReturnType<typeof goToItemSelectionPage>
| ReturnType<typeof setOwnershipStatus>
| ReturnType<typeof setAdditionalDetailsStatus>;
